<template>
  <div class="store-locations critical-component-clear">
    <div class="store-locations__container">
      <div class="store-locations__header text-center">
        <h2
          class="store-locations__title text-heading-l"
          v-text="liquid.title"
        />

        <div
          class="store-locations__copy"
          v-html="liquid.bodyText"
        />

        <btn
          class="store-locations__cta"
          class-label="text-other-button-l"
          modifiers="text"
          :label="liquid.ctaText"
          :url="liquid.ctaLink"
          show-icon
        />
      </div>

      <div class="store-locations__selector">
        <div class="store-locations__selector">
          <div class="form-group">
            <label
              class="visually-hidden"
              for="store-locator-region"
              v-text="$string('locations.select')"
            />

            <select
              id="store-locator-region"
              v-model="userSelectedRegion"
              class="store-location__select"
              @change="handleSelectChange"
            >
              <option
                v-for="(region, index) of liquid.regions"
                :key="index"
                :selected="userSelectedRegion === region.code"
                :value="region.code"
                v-text="region.label"
              />
            </select>
          </div>
        </div>
      </div>

      <div
        class="store-locations__carousel"
      >
        <splide
          :id="`store-locations__stores--${liquid.sectionId}`"
          :has-track="false"
          :options="localConfig"
          @splide:mounted="handleSplideMounted"
          @splide:moved="handleSplideMoved"
        >
          <!-- mobile slides -->
          <splide-track v-if="isMobile">
            <splide-slide
              v-for="(block, index) of mobileLocations"
              :key="index"
              class="store-locations__stores"
            >
              <div class="store-locations__store">
                <div class="store-locations__store-thumbnail-wrapper">
                  <a
                    v-if="block.reservationURL"
                    :href="block.reservationURL"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <responsive-image
                      v-if="block.mobileImage"
                      class="store-locations__image primary-image"
                      :image="block.mobileImage"
                      :lazyload="false"
                      ratio="1:1"
                    />
                    <responsive-image
                      v-else
                      class="store-locations__image primary-image"
                      :image="block.primaryImage"
                      :lazyload="false"
                      ratio="1:1"
                    />
                    <span
                      class="visually-hidden"
                      v-text="`${block.location}: ${block.reservationURL}`"
                    />
                  </a>
                  <div
                    v-else
                  >
                    <responsive-image
                      v-if="block.mobileImage"
                      class="store-locations__image primary-image"
                      :image="block.mobileImage"
                      :lazyload="false"
                      ratio="1:1"
                    />
                    <responsive-image
                      v-else
                      class="store-locations__image primary-image"
                      :image="block.primaryImage"
                      :lazyload="false"
                      ratio="1:1"
                    />
                  </div>
                </div>

                <div class="store-locations__store-content text-center">
                  <h3
                    v-if="block.city"
                    class="store-locations__store-city text-other-label-l"
                    v-text="block.city"
                  />

                  <h4
                    v-if="block.location"
                  >
                    <a
                      v-if="block.reservationURL"
                      :href="block.reservationURL"
                      class="store-locations_store-location text-heading-3xl"
                      target="_blank"
                      rel="noopener noreferrer"
                      v-text="block.location"
                    />
                    <span
                      v-else
                      class="store-locations_store-location text-heading-3xl"
                      v-text="block.location"
                    />
                  </h4>

                  <div
                    v-if="block.address"
                    class="store-locations__store-address"
                    v-html="block.address"
                  />

                  <div
                    v-if="block.phone"
                    class="store-locations__phone text-body-s-regular"
                    v-text="block.phone"
                  />

                  <btn
                    v-if="block.reservationURL && block.reservationLabel"
                    class="store-locations__cta"
                    class-label="text-other-button-l"
                    modifiers="text"
                    :label="block.reservationLabel"
                    :url="block.reservationURL"
                    show-icon
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                </div>
              </div>
            </splide-slide>
          </splide-track>

          <!-- desktop slides -->
          <splide-track v-if="!isMobile">
            <splide-slide
              v-for="(page, pageIndex) of locations"
              :key="pageIndex"
              class="store-locations__stores"
            >
              <div
                v-for="(block, index) of page"
                :key="index"
                class="store-locations__store"
              >
                <div class="store-locations__store-thumbnail-wrapper">
                  <a
                    v-if="block.reservationURL"
                    :href="block.reservationURL"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <responsive-image
                      class="store-locations__image primary-image"
                      :image="block.primaryImage"
                      :lazyload="false"
                      ratio="4:5"
                    />

                    <responsive-image
                      class="store-locations__image secondary-image"
                      :image="block.secondaryImage"
                      :lazyload="false"
                      ratio="4:5"
                    />
                    <span
                      class="visually-hidden"
                      v-text="`${block.location}: ${block.reservationURL}`"
                    />
                  </a>

                  <div v-else>
                    <responsive-image
                      class="store-locations__image primary-image"
                      :image="block.primaryImage"
                      :lazyload="false"
                      ratio="4:5"
                    />

                    <responsive-image
                      class="store-locations__image secondary-image"
                      :image="block.secondaryImage"
                      :lazyload="false"
                      ratio="4:5"
                    />
                  </div>
                </div>

                <div class="store-locations__store-content text-center">
                  <h3
                    class="store-locations__store-city text-other-label-l"
                    v-text="block.city"
                  />

                  <h4
                    v-if="block.location"
                  >
                    <a
                      v-if="block.reservationURL"
                      :href="block.reservationURL"
                      class="store-locations_store-location text-heading-3xl"
                      target="_blank"
                      rel="noopener noreferrer"
                      v-text="block.location"
                    />
                    <span
                      v-else
                      class="store-locations_store-location text-heading-3xl"
                      v-text="block.location"
                    />
                  </h4>

                  <div
                    class="store-locations__store-address"
                    v-html="block.address"
                  />
                  <div
                    class="store-locations__phone text-body-s-regular"
                    v-text="block.phone"
                  />

                  <btn
                    v-if="block.reservationLabel"
                    class="store-locations__cta"
                    class-label="text-other-button-l"
                    modifiers="text"
                    :label="block.reservationLabel"
                    :url="block.reservationURL"
                    target="_blank"
                    rel="noopener noreferrer"
                    show-icon
                  />
                </div>
              </div>
            </splide-slide>
          </splide-track>

          <div
            v-if="isMobile"
            class="store-locations__arrows splide__arrows"
          >
            <button
              class="splide__arrow splide__arrow--prev"
              type="button"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.carousel.previous')"
              />

              <arrow-left />
            </button>

            <div
              class="
                store-locations__counter
                splide-arrows__counter
                text-body-s-regular
              "
              v-text="`${localSlideIndex + 1}/${mobileLocations.length}`"
            />

            <button
              class="splide__arrow splide__arrow--next"
              type="button"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.carousel.next')"
              />

              <arrow-right />
            </button>
          </div>

          <div
            v-if="!isMobile"
            class="store-locations__arrows splide__arrows"
          >
            <button
              class="splide__arrow splide__arrow--prev"
              type="button"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.carousel.previous')"
              />

              <arrow-left />
            </button>

            <button
              class="splide__arrow splide__arrow--next"
              type="button"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.carousel.next')"
              />

              <arrow-right />
            </button>
          </div>

          <ul
            v-if="!isMobile"
            class="store-locations__pagination splide__pagination"
          />
        </splide>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Store locations (store-locations)
 * -----------------------------------------------------------------------------
 * List of store locations.
 * - Component settings: section / dynamic / async / scroll
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import { mapGetters, mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import ResponsiveImage from '~global/images/responsive-image'
import Btn from '~global/btn/btn'
import ArrowRight from '~icons/directional-navigation/arrow/right.svg'
import ArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'StoreLocations',
  components: {
    Splide,
    SplideSlide,
    SplideTrack,
    ResponsiveImage,
    Btn,
    ArrowRight,
    ArrowLeft,
  },
  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      storesPerPage: 4,
      localSlideIndex: 0,
      locations: [],
      mobileLocations: [],
      userSelectedRegion: '',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getRtlWritingDirection: 'getRtlWritingDirection',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    config() {
      const config = {
        arrowPath: 'M14 19.5C13.7239 19.5 13.5 19.7239 13.5 20C13.5 20.2761 13.7239 20.5 14 20.5V19.5ZM26.3536 20.3536C26.5488 20.1583 26.5488 19.8417 26.3536 19.6464L23.1716 16.4645C22.9763 16.2692 22.6597 16.2692 22.4645 16.4645C22.2692 16.6597 22.2692 16.9763 22.4645 17.1716L25.2929 20L22.4645 22.8284C22.2692 23.0237 22.2692 23.3403 22.4645 23.5355C22.6597 23.7308 22.9763 23.7308 23.1716 23.5355L26.3536 20.3536ZM14 20.5H26V19.5H14V20.5Z" fill="#000E34"',
        direction: this.getRtlWritingDirection ? 'rtl' : 'ltr',
        drag: true,
        flickPower: 200,
        flickMaxPages: 1,
        mediaQuery: 'min',
        pagination: !this.isMobile,
        speed: this.$timing('normal'),
        start: 0,
      }
      return config
    },

    /**
     * Compute local config.
     * @returns {Object}
     */
    localConfig() {
      const localConfig = {
        drag: true,
        gap: '1rem',
        perPage: 1.25,
        perMove: 1,
        omitEnd: true,
        focus: 0,
        trimSpace: true,
        padding: {
          right: '16px',
          left: '16px',
        },
        breakpoints: {
          [breakpoints.l]: {
            gap: 0,
            perPage: 1,
            ...this.config,
          },
        },
        ...this.config,
      }
      return localConfig
    },
  },

  watch: {
    locations() {
      this.handleSplideProgress(0)
    },
  },

  mounted() {
    this.setDefaultRegion()
    this.filterLocationsByRegion(this.userSelectedRegion)
    this.formatLocations(this.locations, this.storesPerPage)
  },

  methods: {

    /**
     * Get first region in list of region codes
     */
    setDefaultRegion() {
      this.userSelectedRegion = this.liquid.regions[0].code
    },

    /**
     * Get list of locations and break array into groups of N values
     * @param {Array} locations - list of locations
     * @param {Number} size - how many stores per page
     */
    formatLocations(locations, size) {
      const data = []

      for (let int = 0; int < locations.length; int++) {
        if (int % size === 0) {
          data.push([locations[int]])
        } else {
          data[data.length - 1].push(locations[int])
        }
      }

      this.locations = data
    },

    /**
     * Get list of locations and break array into groups of N values
     * @param {String} region - selected region
     */
    filterLocationsByRegion(region) {
      this.locations = this.blocks.filter((block) => block.region === region)
      this.mobileLocations = this.locations
    },

    /**
     * Select drop down handler
     */
    handleSelectChange() {
      this.filterLocationsByRegion(this.userSelectedRegion)
      this.formatLocations(this.locations, 4)
    },

    /**
     * Handle Splide mounted event.
     * @param {Object} splide - Splide instance.
     */
    handleSplideMounted(splide) {
      this.handleSplideProgress(splide.index)
    },

    /**
     * Handle Splide moved event.
     * - Updates current slide index to share with modal.
     * - Sets pinch zoom container if zoom prop is active.
     * @param {Object} splide - Splide instance.
     * @param {Number} newIndex - New slide index.
     */
    handleSplideMoved(splide, newIndex) {
      this.localSlideIndex = newIndex
      this.handleSplideProgress(this.localSlideIndex + 1)
    },

    /**
     * Handle splide progress bar animation
     * @param {Number} index - Splide current index.
     */
    handleSplideProgress(index) {
      const end = this.locations.length
      const rate = Math.min((index + 1) / end, 1)

      if (!this.$refs.progressBar) {
        return
      }
      this.$refs.progressBar.style.width = `${String(100 * rate)}%`
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './store-locations';
</style>
